/**
 * Application shell (ADR-0012 §3).
 *
 * The layout is expressed with logical properties and a single `lg` breakpoint, so one markup tree serves desktop and
 * mobile in both directions:
 *
 * - below `lg` the sidebar is an off-canvas drawer, opened from the top bar;
 * - from `lg` it is a column in the flow that can be collapsed to an icon rail.
 *
 * Two accessibility requirements are implemented here rather than left to each page: a skip link as the first
 * focusable element (WCAG 2.4.1), and `tabIndex={-1}` on `<main>` so the skip link can actually move focus.
 */

import { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { Sidebar } from '@/components/layout/Sidebar';
import { Topbar } from '@/components/layout/Topbar';
import { Skeleton } from '@/components/ui/surface';
import { IdentityPendingBanner } from '@/features/auth/components/IdentityPendingBanner';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';

/** Route-level loading placeholder; shaped like a page so the layout does not jump when content arrives. */
function PageFallback() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6" aria-busy="true">
      <span className="sr-only">{t('common.loading')}</span>
      <Skeleton className="h-8 w-56" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((key) => (
          <Skeleton key={key} className="h-28" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

/** Off-canvas navigation drawer used below the `lg` breakpoint. */
function MobileDrawer() {
  const { t } = useTranslation();
  const isOpen = useUiStore((state) => state.mobileNavOpen);
  const setOpen = useUiStore((state) => state.setMobileNavOpen);
  const panelRef = useRef<HTMLDivElement>(null);

  // `Escape` must close the drawer, and focus must move into it: a modal panel that leaves focus behind is a
  // keyboard trap in reverse — the user can no longer reach the visible content.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <button
        type="button"
        // A button, not a div: the backdrop is a real control and must be reachable by keyboard.
        aria-label={t('nav.closeMenu')}
        className="absolute inset-0 h-full w-full cursor-default bg-foreground/40"
        onClick={() => setOpen(false)}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.workspaceSection')}
        className="absolute inset-y-0 start-0 w-72 max-w-[80vw] border-e border-sidebar-border shadow-lg"
      >
        <Sidebar />
      </div>
    </div>
  );
}

/** Chrome shared by every authenticated screen. */
export function AppShell() {
  const { t } = useTranslation();
  const collapsed = useUiStore((state) => state.sidebarCollapsed);

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:start-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        {t('nav.skipToContent')}
      </a>

      <div className="flex min-h-screen">
        <aside
          className={cn(
            'hidden shrink-0 border-e border-sidebar-border lg:block',
            collapsed ? 'w-16' : 'w-64',
          )}
        >
          <div className="sticky top-0 h-screen overflow-y-auto">
            <Sidebar />
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar />

          <IdentityPendingBanner />

          <main id="main-content" tabIndex={-1} className="flex-1 p-4 focus:outline-none lg:p-6">
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>

      <MobileDrawer />
    </div>
  );
}
